import React, { useState, useEffect } from 'react';
import Sidebar from '../components/shared/Sidebar';
import DataGrid, { GridColumn, GridRow } from '../components/data/DataGrid';
import useAuthStore from '../store/authStore';
import { UserAccess, AccessScope } from '../types/userAccess';
import '../components/Modal.css';

interface AccessOptions {
  organizations: Array<{ id: number; name: string; subdomain?: string }>;
  hierarchyLevels: Array<{ id: number; name: string; organization_id: number; organizationName?: string }>;
  hierarchyNodes: Array<{ id: number; name: string; hierarchy_level_id: number; levelName?: string; organizationName?: string }>;
  locations: Array<{ id: number; name: string; organization_id?: number; organizationName?: string }>;
}

interface User {
  id: number;
  name: string;
  email: string;
  role?: string;
  organization_id?: number;
  is_super_admin?: boolean;
}

const UserAccessManagementPage: React.FC = () => {
  const { user } = useAuthStore();
  const [userAccessList, setUserAccessList] = useState<UserAccess[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [accessOptions, setAccessOptions] = useState<AccessOptions>({
    organizations: [],
    hierarchyLevels: [],
    hierarchyNodes: [],
    locations: []
  });
  const [loading, setLoading] = useState(true);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [editingAccess, setEditingAccess] = useState<UserAccess | null>(null);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<UserAccess | null>(null);

  const [formData, setFormData] = useState({
    userId: '',
    accessScope: 'location' as AccessScope,
    targetId: ''
  });

  useEffect(() => {
    fetchUserAccess();
    fetchUsers();
    fetchAccessOptions();
  }, []);

  const fetchUserAccess = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/users`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch user access: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setUserAccessList(data.data);
      } else {
        throw new Error(data.message || 'Failed to fetch user access');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error fetching user access:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/available-users?excludeAdmins=true`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setUsers(data.data);
      }
    } catch (err: any) {
      console.error('Error fetching users:', err);
    }
  };

  const fetchAccessOptions = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/options`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch access options: ${response.status}`);
      }

      const data = await response.json();
      if (data.success) {
        setAccessOptions(data.data);
      }
    } catch (err: any) {
      console.error('Error fetching access options:', err);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreating(true);
    setError(null);
    setSuccess(null);

    try {
      if (!formData.userId || !formData.accessScope || !formData.targetId) {
        throw new Error('All fields are required');
      }

      const payload: any = {
        user_id: parseInt(formData.userId),
        access_scope: formData.accessScope
      };

      // Set the appropriate ID based on access scope
      switch (formData.accessScope) {
        case 'organization':
          payload.organization_id = parseInt(formData.targetId);
          break;
        case 'hierarchy_node':
          payload.hierarchy_node_id = parseInt(formData.targetId);
          break;
        case 'location':
          payload.location_id = parseInt(formData.targetId);
          break;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/users`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to assign user access');
      }

      if (data.success) {
        setSuccess(`User access assigned successfully!`);
        setFormData({ userId: '', accessScope: 'location', targetId: '' });
        setShowAssignForm(false);
        setEditingAccess(null);
        fetchUserAccess();
      } else {
        throw new Error(data.message || 'Failed to assign user access');
      }
    } catch (err: any) {
      setError(err.message);
      console.error('Error assigning user access:', err);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (userAccess: UserAccess) => {
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001/api'}/user-access/users/${userAccess.user_id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to remove user access');
      }

      setSuccess('User access removed successfully!');
      setShowDeleteConfirm(null);
      fetchUserAccess();
    } catch (err: any) {
      setError(err.message || 'Failed to remove user access');
      setShowDeleteConfirm(null);
    }
  };

  const startEdit = (userAccess: UserAccess) => {
    setEditingAccess(userAccess);
    setFormData({
      userId: String(userAccess.user_id),
      accessScope: userAccess.access_scope,
      targetId: String(
        userAccess.access_scope === 'organization' ? userAccess.organization_id :
        userAccess.access_scope === 'hierarchy_node' ? userAccess.hierarchy_node_id :
        userAccess.location_id
      ) || ''
    });
    setShowAssignForm(true);
  };

  const cancelEdit = () => {
    setEditingAccess(null);
    setFormData({ userId: '', accessScope: 'location', targetId: '' });
    setShowAssignForm(false);
  };

  const getAccessTargetName = (userAccess: UserAccess): string => {
    switch (userAccess.access_scope) {
      case 'organization':
        return userAccess.organizationName || 'Unknown Organization';
      case 'hierarchy_node':
        return userAccess.hierarchyNodeName || 'Unknown Node';
      case 'location':
        return userAccess.locationName || 'Unknown Location';
      default:
        return 'Unknown';
    }
  };

  const getTargetOptions = () => {
    switch (formData.accessScope) {
      case 'organization':
        return accessOptions.organizations.map(org => ({
          value: String(org.id),
          label: org.name
        }));
      case 'hierarchy_node':
        return accessOptions.hierarchyNodes.map(node => ({
          value: String(node.id),
          label: `${node.name} (${node.levelName || 'Unknown Level'})`
        }));
      case 'location':
        return accessOptions.locations.map(location => ({
          value: String(location.id),
          label: `${location.name}${location.organizationName ? ` - ${location.organizationName}` : ''}`
        }));
      default:
        return [];
    }
  };

  const getUnassignedUsers = () => {
    const assignedUserIds = new Set(userAccessList.map(ua => ua.user_id));
    
    // Backend already excludes organization admins and super admins
    // We just need to filter out users who already have access assignments
    return users.filter(user => !assignedUserIds.has(user.id));
  };

  return (
    <div className="dashboard-page">
      <Sidebar />
      <div className="dashboard-main">
        <div className="dashboard-content">
          <div className="page-header">
            <h1>User Access Management</h1>
            <p>
              {user?.is_super_admin 
                ? 'Assign users to organizations, hierarchy nodes, or specific locations. Organization admins and super admins have full access by default and do not need assignments.' 
                : 'Assign users within your organization to different access levels. Organization admins have full access by default and do not appear in this list.'
              }
            </p>
          </div>

          {error && (
            <div className="alert alert-error">
              <span>{error}</span>
              <button onClick={() => setError(null)} className="alert-close">×</button>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <span>{success}</span>
              <button onClick={() => setSuccess(null)} className="alert-close">×</button>
            </div>
          )}

          <div className="card">
            <div className="card-header">
              <h2>User Access Assignments ({userAccessList.length})</h2>
              <button 
                className="primary-button"
                onClick={() => setShowAssignForm(true)}
                disabled={creating}
              >
                Assign User Access
              </button>
            </div>
            <div className="card-content">
              {loading ? (
                <div className="loading-state">
                  <div className="loading-spinner"></div>
                  <p>Loading user access assignments...</p>
                </div>
              ) : userAccessList.length === 0 ? (
                <div className="empty-state">
                  <h3>No User Access Assignments</h3>
                  <p>Start by assigning users to different access levels.</p>
                  <button 
                    className="primary-button"
                    onClick={() => setShowAssignForm(true)}
                  >
                    Assign First User
                  </button>
                </div>
              ) : (
                <DataGrid
                  columns={[
                    {
                      field: 'userName',
                      headerName: 'User',
                      width: 180,
                      renderCell: (row: GridRow) => (
                        <span>{row.userName || 'Unknown User'}</span>
                      )
                    },
                    {
                      field: 'access_scope',
                      headerName: 'Access Type',
                      width: 140,
                      renderCell: (row: GridRow) => (
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          fontSize: '12px',
                          backgroundColor: 
                            row.access_scope === 'organization' ? '#e3f2fd' :
                            row.access_scope === 'hierarchy_node' ? '#f3e5f5' :
                            '#e8f5e8',
                          color:
                            row.access_scope === 'organization' ? '#1976d2' :
                            row.access_scope === 'hierarchy_node' ? '#7b1fa2' :
                            '#388e3c'
                        }}>
                          {row.access_scope === 'organization' ? 'Organization' :
                           row.access_scope === 'hierarchy_node' ? 'Hierarchy Node' :
                           'Single Location'}
                        </span>
                      )
                    },
                    {
                      field: 'accessTarget',
                      headerName: 'Access Target',
                      width: 220,
                      renderCell: (row: GridRow) => (
                        <span>{getAccessTargetName(row as UserAccess)}</span>
                      )
                    },
                    {
                      field: 'role_name',
                      headerName: 'Role',
                      width: 150,
                      renderCell: (row: GridRow) => (
                        <span>{row.role_name || 'Unknown Role'}</span>
                      )
                    },
                    {
                      field: 'assigned_at',
                      headerName: 'Assigned',
                      width: 120,
                      type: 'date',
                      renderCell: (row: GridRow) => (
                        <span>{row.assigned_at ? new Date(row.assigned_at).toLocaleDateString() : '-'}</span>
                      )
                    },
                    {
                      field: 'actions',
                      headerName: 'Actions',
                      width: 200,
                      renderCell: (row: GridRow) => (
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button 
                            className="secondary-button"
                            onClick={() => startEdit(row as UserAccess)}
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                          >
                            Edit
                          </button>
                          <button 
                            className="delete-button"
                            onClick={() => setShowDeleteConfirm(row as UserAccess)}
                            style={{ fontSize: '12px', padding: '6px 12px' }}
                          >
                            Remove
                          </button>
                        </div>
                      )
                    }
                  ]}
                  rows={userAccessList.map(access => ({ ...access }))}
                  height="400px"
                  loading={loading}
                  className="user-access-grid"
                />
              )}
            </div>
          </div>

          {/* Assign/Edit User Access Modal */}
          {showAssignForm && (
            <div className="modal-overlay" onClick={() => cancelEdit()}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>{editingAccess ? 'Edit User Access' : 'Assign User Access'}</h2>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="form-group">
                      <label htmlFor="userId">User *</label>
                      <select
                        id="userId"
                        name="userId"
                        value={formData.userId}
                        onChange={handleInputChange}
                        required
                        disabled={!!editingAccess}
                      >
                        <option value="">Select a user</option>
                        {editingAccess ? (
                          <option value={String(editingAccess.user_id)}>
                            {editingAccess.userName}
                          </option>
                        ) : (
                          getUnassignedUsers().map(user => (
                            <option key={user.id} value={String(user.id)}>
                              {user.name} ({user.email})
                            </option>
                          ))
                        )}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="accessScope">Access Type *</label>
                      <select
                        id="accessScope"
                        name="accessScope"
                        value={formData.accessScope}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="location">Single Location Access</option>
                        <option value="hierarchy_node">Hierarchy Node Access</option>
                        <option value="organization">Full Organization Access</option>
                      </select>
                      <small style={{ color: '#888', fontSize: '12px' }}>
                        {formData.accessScope === 'location' && 'User will only see data from one specific location'}
                        {formData.accessScope === 'hierarchy_node' && 'User will see data from locations under specific hierarchy nodes'}
                        {formData.accessScope === 'organization' && 'User will see all data within the organization'}
                      </small>
                    </div>

                    <div className="form-group">
                      <label htmlFor="targetId">
                        {formData.accessScope === 'organization' ? 'Organization' :
                         formData.accessScope === 'hierarchy_node' ? 'Hierarchy Node' :
                         'Location'} *
                      </label>
                      <select
                        id="targetId"
                        name="targetId"
                        value={formData.targetId}
                        onChange={handleInputChange}
                        required
                      >
                        <option value="">
                          Select a {formData.accessScope === 'organization' ? 'organization' :
                                   formData.accessScope === 'hierarchy_node' ? 'hierarchy node' :
                                   'location'}
                        </option>
                        {getTargetOptions().map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </form>
                </div>
                <div className="modal-actions">
                  <button 
                    type="submit" 
                    className="primary-button"
                    onClick={handleSubmit}
                    disabled={creating}
                    style={{ minWidth: '120px', flex: 'none', height: '44px', padding: '12px 16px' }}
                  >
                    {creating ? 'Assigning...' : (editingAccess ? 'Update Access' : 'Assign Access')}
                  </button>
                  <button 
                    type="button" 
                    className="secondary-button"
                    onClick={cancelEdit}
                    disabled={creating}
                    style={{ minWidth: '100px', flex: 'none', height: '44px', padding: '12px 16px' }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
              <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Remove User Access</h2>
                </div>
                <div className="modal-body">
                  <p>Are you sure you want to remove access for <strong>{showDeleteConfirm.userName}</strong>?</p>
                  <p style={{ color: '#ff6b6b', fontSize: '14px' }}>
                    ⚠️ This user will lose access to their assigned {showDeleteConfirm.access_scope === 'organization' ? 'organization' : showDeleteConfirm.access_scope === 'hierarchy_node' ? 'hierarchy node' : 'location'}.
                  </p>
                </div>
                <div className="modal-actions">
                  <button 
                    className="delete-button"
                    onClick={() => handleDelete(showDeleteConfirm)}
                  >
                    Remove Access
                  </button>
                  <button 
                    className="secondary-button"
                    onClick={() => setShowDeleteConfirm(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserAccessManagementPage;